import type {
  FilterBarItem,
  FilterItem,
} from '@wconcept/wconcept-common/models/renewal/common';
import { setComma } from '@wconcept/wconcept-common/utils/commonUtils';
import Box from '@wconcept/wconcept-ui-renewal/components/atoms/Box';
import Text from '@wconcept/wconcept-ui-renewal/components/atoms/Text';
import {
  TabPanel,
  Tabs,
} from '@wconcept/wconcept-ui-renewal/components/molecules/Tabs';
import ModalEdgeCloseBtn from '@wconcept/wconcept-ui-renewal/components/molecules/_btn/ModalEdgeCloseBtn';
import ModalGroupBtn from '@wconcept/wconcept-ui-renewal/components/molecules/_btn/ModalGroupBtn';
import ErrorCont from '@wconcept/wconcept-ui-renewal/components/molecules/ErrorCont';
import PopupHeader from '@wconcept/wconcept-ui-renewal/components/organisms/_header/PopupHeader';
import ColorContent from '@wconcept/wconcept-ui-renewal/src/components/organisms/_modal/LpFilterModal/ColorContent';
import { FilterBar } from '@wconcept/wconcept-ui-renewal/src/components/organisms/_modal/LpFilterModal/FilterBar';
import StyleContent from '@wconcept/wconcept-ui-renewal/src/components/organisms/_modal/LpFilterModal/StyleContent';
import TpoContent from '@wconcept/wconcept-ui-renewal/src/components/organisms/_modal/LpFilterModal/TpoContent';
import BrandContent from '@wconcept/wconcept-ui-renewal/src/components/organisms/_modal/LpFilterModal/BrandContent';
import CategoryContent from '@wconcept/wconcept-ui-renewal/src/components/organisms/_modal/LpFilterModal/CategoryContent';
import { useCallback, useMemo, useState } from 'react';
import * as S from '@wconcept/wconcept-ui-renewal/src/components/organisms/_modal/FilterModal/style';
import type {
  LpProductCountResult,
  LpProductFilterResult,
  LpProductFilterType,
  LpProductFilters,
  LpProductRequest,
} from '@apis/renewal/lpProduct';
import {
  getLpProductBrandOptions,
  getUniqueLpProductFilterOptions,
} from '@apis/renewal/lpProduct';
import { getLpVisibleSelectedFilters } from '@utils/lpFilterBarModel';
import ProductContentV2 from './components/ProductContentV2';
import PriceContentV2 from './components/PriceContentV2';
import {
  getLpProductV2EmptyState,
  isLpProductV2FilterBarItemSelected,
  selectLpProductV2CategoryPath,
  sortLpProductV2BrandFilterItems,
  toggleLpProductV2BrandFilter,
  toggleLpProductV2CategoryFilter,
} from './filterV2';
import { useLpFilterModalData } from './hooks/useLpFilterModalData';

const FILTER_ERROR_MESSAGE = {
  network: '네트워크 연결이 원활하지 않습니다.<br/>연결 상태를 확인해 주세요.',
  service:
    '현재 서비스 접속이​ 원활하지 않습니다.<br/>이용에 불편을 드려 죄송합니다.',
} as const;

interface LpFilterModalV2Props {
  readonly onSubmit?: (selectedFilters: FilterItem[]) => void;
  readonly onClose?: () => void;
  readonly value: {
    readonly selectedFilterList: FilterItem[];
    readonly selectedTab: string;
    readonly onTabChange?: (tab: string) => void;
    readonly isDesktop: boolean;
    readonly isIPhoneXApp: boolean;
    readonly tabListData: FilterBarItem[];
    readonly originalFilters: LpProductFilters;
    readonly requestFilter: (
      type: LpProductFilterType,
      params: LpProductRequest
    ) => Promise<LpProductFilterResult | undefined>;
    readonly requestCount: (
      params: LpProductRequest
    ) => Promise<LpProductCountResult | undefined>;
    readonly hideFilterOptionCounts?: boolean;
    readonly hiddenSelectedFilterTypes?: readonly string[];
  };
}

const getInitialTab = (
  selectedTab: string,
  tabListData: readonly FilterBarItem[]
): string =>
  selectedTab || tabListData.find(item => item.visible !== false)?.value || '';

const toFilterItems = (
  options:
    | readonly {
        readonly code: string | number;
        readonly name: string;
        readonly count: number;
      }[]
    | null
    | undefined
): FilterItem[] =>
  (options ?? []).map(option => ({
    code: String(option.code),
    name: option.name,
    count: option.count,
  }));

function LpFilterModalV2({
  onSubmit,
  onClose,
  value,
}: LpFilterModalV2Props): React.JSX.Element {
  const {
    selectedFilterList,
    selectedTab,
    onTabChange,
    isDesktop,
    isIPhoneXApp,
    tabListData,
    originalFilters,
    requestFilter,
    requestCount,
    hideFilterOptionCounts,
    hiddenSelectedFilterTypes,
  } = value;
  const [tab, setTab] = useState(() => getInitialTab(selectedTab, tabListData));
  const [selectedFilters, setSelectedFilters] = useState(selectedFilterList);
  const [brandSortFilters, setBrandSortFilters] = useState(selectedFilterList);
  const [brandTab, setBrandTab] = useState('topBrand');

  const {
    filters,
    total,
    isLoading,
    isNetworkError,
    isServiceError,
    retryRequests,
  } = useLpFilterModalData({
    tab,
    brandTab,
    selectedFilters,
    originalFilters,
    requestFilter,
    requestCount,
  });

  const selectedProductCodes = useMemo(
    () =>
      selectedFilters.flatMap(filter =>
        (filter.filterType === 'productType' ||
          filter.filterType === 'benefit') &&
        filter.code
          ? [filter.code]
          : []
      ),
    [selectedFilters]
  );
  const selectedFilterBarItems = getLpVisibleSelectedFilters(
    selectedFilters,
    tabListData,
    hiddenSelectedFilterTypes
  );
  const filterModalClassName = [
    selectedFilterBarItems.length > 0 ? 'isFilterList' : '',
    isLoading ? 'isLoading' : '',
    isIPhoneXApp ? 'is-old-app-iphonex' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const isSelected = useCallback(
    (filterType: string, item: FilterItem): boolean =>
      selectedFilters.some(
        filter => filter.filterType === filterType && filter.code === item.code
      ),
    [selectedFilters]
  );

  const toggleFilter = useCallback((filterType: string, item: FilterItem) => {
    setSelectedFilters(current => {
      const selected = current.some(
        filter => filter.filterType === filterType && filter.code === item.code
      );

      if (selected) {
        return current.filter(
          filter =>
            filter.filterType !== filterType || filter.code !== item.code
        );
      }

      return [...current, { ...item, filterType }];
    });
  }, []);

  const changeCategory = useCallback((item: FilterItem) => {
    setSelectedFilters(current =>
      toggleLpProductV2CategoryFilter(current, item)
    );
  }, []);

  const changeCategoryPath = useCallback((item: FilterItem) => {
    setSelectedFilters(current => selectLpProductV2CategoryPath(current, item));
  }, []);

  const changeBrand = useCallback((filterType: string, item: FilterItem) => {
    setSelectedFilters(current =>
      toggleLpProductV2BrandFilter(current, filterType, item)
    );
  }, []);

  const isBrandSelected = useCallback(
    (filterTypes: string[], item: FilterItem): boolean =>
      selectedFilters.some(
        filter =>
          filterTypes.includes(filter.filterType ?? '') &&
          filter.code === item.code
      ),
    [selectedFilters]
  );

  const changePrice = useCallback((price: FilterItem) => {
    setSelectedFilters(current => [
      ...current.filter(filter => filter.filterType !== 'priceRange'),
      ...(price.name ? [{ ...price, filterType: 'priceRange' }] : []),
    ]);
  }, []);

  const deleteSelectedFilter = useCallback((item: FilterItem): void => {
    setSelectedFilters(current =>
      current.filter(
        filter =>
          filter.filterType !== item.filterType || filter.code !== item.code
      )
    );
  }, []);

  const resetSelectedFilters = useCallback((): void => {
    setSelectedFilters(current =>
      current.filter(filter =>
        hiddenSelectedFilterTypes?.includes(filter.filterType ?? '')
      )
    );
  }, [hiddenSelectedFilterTypes]);

  const isTabSelected = useCallback(
    (item: FilterBarItem): boolean =>
      isLpProductV2FilterBarItemSelected(item, selectedFilters),
    [selectedFilters]
  );

  const changeTab = useCallback(
    (nextTab: string): void => {
      if (nextTab === 'topBrand') setBrandSortFilters(selectedFilters);
      setTab(nextTab);
      onTabChange?.(nextTab);
    },
    [onTabChange, selectedFilters]
  );

  const changeBrandTab = useCallback(
    (nextBrandTab: string): void => {
      setBrandSortFilters(selectedFilters);
      setBrandTab(nextBrandTab);
    },
    [selectedFilters]
  );

  const renderContent = (): React.ReactNode => {
    if (isNetworkError || isServiceError) {
      return (
        <ErrorCont
          type="filter"
          title={
            isNetworkError
              ? FILTER_ERROR_MESSAGE.network
              : FILTER_ERROR_MESSAGE.service
          }
          linkText="다시 시도하기"
          onClick={retryRequests}
        />
      );
    }

    const emptyState = getLpProductV2EmptyState(tab, filters, selectedFilters);
    if (emptyState) {
      return (
        <Box className={emptyState.className}>
          <Text className="box-msg" paragraph>
            {emptyState.message}
          </Text>
        </Box>
      );
    }

    switch (tab) {
      case 'category':
        return (
          <CategoryContent
            selectedFilters={selectedFilters}
            category={toFilterItems(filters.category?.buckets)}
            rootCategory={toFilterItems(filters.rootCategory?.slice(0, 3))}
            isItemSelected={isSelected}
            handleCateegoryPathClick={changeCategoryPath}
            onChangeCategory={changeCategory}
            noListMsg={
              <Text className="box-msg" paragraph>
                {'해당되는 카테고리가 없습니다.<br>선택한 필터를 변경해보세요.'}
              </Text>
            }
            hideFilterOptionCounts={hideFilterOptionCounts}
          />
        );
      case 'color':
        return (
          <ColorContent
            colorList={filters.colors ? [...filters.colors] : undefined}
            isItemSelected={isSelected}
            onChangeColor={item => toggleFilter('color', item)}
            hideFilterOptionCounts={hideFilterOptionCounts}
          />
        );
      case 'priceRange':
        return (
          <PriceContentV2
            selectedPrice={
              selectedFilters.find(
                filter => filter.filterType === 'priceRange'
              ) || {}
            }
            priceRange={filters.priceRange}
            onChangePrice={changePrice}
          />
        );
      case 'benefit':
        return (
          <ProductContentV2
            productType={filters.products?.productType}
            benefit={filters.products?.benefit}
            selectedCodes={selectedProductCodes}
            onChange={(groupType, item) => toggleFilter(groupType, item)}
            hideFilterOptionCounts={hideFilterOptionCounts}
          />
        );
      case 'brand':
      case 'topBrand':
      case 'newBrand':
        return (
          <BrandContent
            selectedValue={brandTab}
            handleTabsBrandChange={changeBrandTab}
            brands={sortLpProductV2BrandFilterItems(
              toFilterItems(getLpProductBrandOptions(filters)),
              brandSortFilters
            )}
            topBrands={sortLpProductV2BrandFilterItems(
              toFilterItems(getUniqueLpProductFilterOptions(filters.topBrand)),
              brandSortFilters
            )}
            newBrands={sortLpProductV2BrandFilterItems(
              toFilterItems(getUniqueLpProductFilterOptions(filters.newBrand)),
              brandSortFilters
            )}
            isItemSelected={isBrandSelected}
            onFilterSelect={changeBrand}
            brandTabs={['brand', 'topBrand', 'newBrand']}
            selectedFilters={selectedFilters}
            noListMsg={
              <Text className="box-msg" paragraph>
                {'해당되는 브랜드가 없습니다.<br>선택한 필터를 변경해보세요.'}
              </Text>
            }
            noSearchMsg={
              <Text className="box-msg" paragraph>
                검색된 브랜드가 없습니다.
              </Text>
            }
            hideFilterOptionCounts={hideFilterOptionCounts}
          />
        );
      case 'style':
        return (
          <StyleContent
            styleList={filters.styles ? [...filters.styles] : undefined}
            isItemSelected={isSelected}
            onChangeStyle={item => toggleFilter('style', item)}
          />
        );
      case 'tpo':
        return (
          <TpoContent
            tpoList={filters.tpos ? [...filters.tpos] : undefined}
            isItemSelected={isSelected}
            onChangeTpo={item => toggleFilter('tpo', item)}
          />
        );
      default:
        return <Box />;
    }
  };

  return (
    <S.FilterModal className={filterModalClassName}>
      {isDesktop ? (
        <PopupHeader onClickClose={onClose} />
      ) : (
        <ModalEdgeCloseBtn onClick={onClose} />
      )}
      <Tabs selectedValue={tab} onChange={changeTab} className="tabs-main">
        {tabListData
          .filter(item => item.visible !== false)
          .map(item => (
            <TabPanel
              key={item.value}
              label={item.text}
              value={item.value}
              className={isTabSelected(item) ? 'dot' : ''}
            >
              {renderContent()}
            </TabPanel>
          ))}
      </Tabs>
      {selectedFilterBarItems.length > 0 && (
        <FilterBar
          list={selectedFilterBarItems}
          onClickDelFilterListItem={deleteSelectedFilter}
          onResetFilterList={resetSelectedFilters}
        />
      )}
      <ModalGroupBtn
        type="rightBig"
        secondaryBtn={{ text: '취소', onClick: onClose }}
        primaryBtn={{
          text: `${setComma(`${total}`)}개 상품보기`,
          onClick: () => onSubmit?.(selectedFilters),
        }}
      />
    </S.FilterModal>
  );
}

export default LpFilterModalV2;
